/**
 * The readable part of a deposit's `notes`, or null when there is none.
 *
 * The server stores that column as one JSON object per line: the capture's own
 * record (`{"source":"capture","notes":"check #12",...}`), then a line for each
 * forfeit or refund. Shown as-is, staff read the raw JSON. This keeps what a
 * person wrote (the capture note, a forfeit reason) and passes any line that is
 * not a JSON object through untouched, since older rows hold free text.
 */
export function readableDepositNotes(notes: string | null | undefined): string | null {
  if (!notes?.trim()) return null;
  const lines = notes.split('\n').flatMap((line) => {
    // A line that is not a JSON object is free text, kept exactly as written:
    // the panel renders pre-wrapped, so its blank lines and indentation read.
    const text = line.trim();
    if (!text) return [line];
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return [line];
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [line];
    const record = parsed as Record<string, unknown>;
    const readable: string[] = [];
    if (typeof record.notes === 'string' && record.notes.trim()) {
      readable.push(record.notes.trim());
    }
    if (typeof record.reason === 'string' && record.reason.trim()) {
      const label =
        'forfeitedAt' in record
          ? 'Forfeited'
          : 'partialForfeitAt' in record
            ? 'Partly forfeited'
            : 'Reason';
      readable.push(`${label}: ${record.reason.trim()}`);
    }
    return readable;
  });
  // Only the edges are trimmed: the newline after the last JSON record, or a
  // record with nothing readable, leaves no blank line behind.
  const readable = lines.join('\n').replace(/^(?:[ \t]*\n)+|(?:\n[ \t]*)+$/g, '');
  return readable.trim() ? readable : null;
}
